import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import ScrollArea from '../ScrollArea/ScrollArea'

import { sendHoverSoundCommand, sendSelectClickSoundCommand } from '../../helpers/web_to_client'

import styles from './Select.css'

const propTypes = {
    caption: PropTypes.node,
    captionPosition: PropTypes.oneOf(['left', 'top']),
    items: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
        ]).isRequired,
        caption: PropTypes.node.isRequired,
        tooltipId: PropTypes.string,
    })).isRequired,
    selectedItem: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]).isRequired,
    width: PropTypes.number,
    isDisabled: PropTypes.bool,
    isJustified: PropTypes.bool,
    onItemChange: PropTypes.func.isRequired,
    onToggled: PropTypes.func,
    tooltipId: PropTypes.string,
}

const defaultProps = {
    captionPosition: 'left',
    onToggled: () => {},
}

import { KEY_CODES } from '../../constants'

const MIN_WIDTH = 40
const MAX_WIDTH = 300
const DROPDOWN_OFFSET = 20

class Select extends React.PureComponent {
    constructor(props) {
        super(props)

        this.state = {
            isActive: false,
            selectWidth: null,
            selectedItem: props.selectedItem,
            dropdownPositionY: 'bottom',
            dropdownPositionX: 'left',
            isDropdownScrollable: false,
            dropdownHeight: 'auto',
        }

        this.handleToggle = this.handleToggle.bind(this)
        this.handleBlur = this.handleBlur.bind(this)
        this.handleKeyDown = this.handleKeyDown.bind(this)
        this.handleItemChange = this.handleItemChange.bind(this)
        this.setPosition = this.setPosition.bind(this)
    }

    componentDidMount() {
        document.addEventListener('keydown', this.handleKeyDown)
        this.updateDropdownWidth()
    }

    componentWillReceiveProps(nextProps) {
        this.updateDropdownWidth()

        if (this.state.selectedItem !== nextProps.selectedItem) {
            this.setState({ selectedItem: nextProps.selectedItem })
        }
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyDown)
    }

    hide() {
        const isActive = false
        this.setState({
            isActive,
            isDropdownScrollable: false,
            dropdownHeight: 'auto',
        })
        this.props.onToggled(isActive)
    }

    selectItem(keyCode) {
        const selectedItemIdx = this.props.items.findIndex(item => item.value === this.state.selectedItem)
        const nextSelectedItemIdx = keyCode === KEY_CODES.ARROW_UP ?
            Math.max(0, selectedItemIdx - 1) :
            Math.min(this.props.items.length - 1, selectedItemIdx + 1)
        const nextSelectedItem = this.props.items[nextSelectedItemIdx].value

        if (this.state.selectedItem !== nextSelectedItem) {
            this.setState({ selectedItem: nextSelectedItem })
        }
    }

    resetItem() {
        if (this.state.selectedItem !== this.props.selectedItem) {
            this.setState({ selectedItem: this.props.selectedItem })
        }
    }

    handleToggle() {
        const isActive = !this.state.isActive

        if (isActive) {
            this.setPosition()
            this.setState({ isActive })
            this.props.onToggled(isActive)
        } else {
            this.hide()
        }
    }

    handleBlur() {
        this.hide()
        this.resetItem()
    }

    handleKeyDown({ keyCode }) {
        if (!this.state.isActive) {
            return
        }

        if (keyCode === KEY_CODES.ESCAPE) {
            this.handleBlur()
        } else if (keyCode === KEY_CODES.ENTER || keyCode === KEY_CODES.SPACE) {
            this.handleItemChange(this.state.selectedItem)
        } else if (keyCode === KEY_CODES.ARROW_UP || keyCode === KEY_CODES.ARROW_DOWN) {
            this.selectItem(keyCode)
        }
    }

    handleItemChange(selectedItem) {
        if (selectedItem !== this.props.selectedItem) {
            this.props.onItemChange(selectedItem)
        } else if (this.state.selectedItem !== this.props.selectedItem) {
            this.resetItem()
        }

        this.hide()
        sendSelectClickSoundCommand()
    }

    updateDropdownWidth() {
        if (this._dropdown) {
            let width = this._dropdown.offsetWidth > MIN_WIDTH ? this._dropdown.offsetWidth : MIN_WIDTH

            this.setState({ selectWidth: width })
        }
    }

    renderItems() {
        const items = this.props.items.map((item, index) => {
            const classNameItem = classNames(styles.item, {
                [styles.isSelected]: this.state.selectedItem === item.value,
            })

            const dataAttrs = item.tooltipId ? {
                'data-tip': true,
                'data-for': item.tooltipId,
            } : {}


            return (
                <li
                    className={classNameItem}
                    key={index}
                    onClick={() => this.handleItemChange(item.value)}
                    onMouseEnter={sendHoverSoundCommand}
                    {...dataAttrs}
                >
                    <span className={styles.itemContainer}>
                        {item.caption}
                    </span>
                </li>
            )
        })

        return this.state.isDropdownScrollable ? (
            <div className={styles.scrollArea}>
                <div className={styles.scrollAreaDecorator}>
                    <ScrollArea scrollableAreaHeight={this.state.dropdownHeight}>
                        {items}
                    </ScrollArea>
                </div>
            </div>
        ) : items
    }

    renderCaption() {
        if (!this.props.caption) {
            return null
        }

        const classNameCaption = classNames(
            styles.caption,
            styles[`caption__${this.props.captionPosition}`]
        )

        return (
            <span className={classNameCaption}>{this.props.caption}</span>
        )
    }

    setPosition() {
        const { clientHeight, clientWidth } = document.documentElement
        const selectOffset = this._select.getBoundingClientRect()
        const dropdownOffsetRight = this._dropdown.getBoundingClientRect().right + DROPDOWN_OFFSET
        const fitToBottom = selectOffset.bottom + this._dropdown.offsetHeight < clientHeight
        const fitToTop = this._dropdown.offsetHeight < selectOffset.top
        let dropdownPositionY = 'bottom'
        let isDropdownScrollable = false
        let dropdownHeight = 'auto'

        if (fitToBottom) {
            dropdownPositionY = 'bottom'
        } else if (fitToTop) {
            dropdownPositionY = 'top'
        } else {
            if (clientHeight - selectOffset.bottom > selectOffset.top) {
                dropdownPositionY = 'bottom'
                dropdownHeight = clientHeight - selectOffset.bottom - DROPDOWN_OFFSET
            } else {
                dropdownPositionY = 'top'
                dropdownHeight = selectOffset.top - DROPDOWN_OFFSET
            }

            isDropdownScrollable = true
        }

        this.setState({
            dropdownPositionX: dropdownOffsetRight > clientWidth ? 'right' : 'left',
            dropdownPositionY,
            isDropdownScrollable,
            dropdownHeight,
        })
    }

    render() {
        const classNameBase = classNames(styles.base, {
            [styles.isActive]: this.state.isActive,
            [styles.isDisabled]: this.props.isDisabled,
            [styles.isJustified]: this.props.isJustified,
        })

        const classNameDropdown = classNames(styles.dropdown, {
            [styles[this.state.dropdownPositionY]]: this.state.dropdownPositionY,
            [styles[this.state.dropdownPositionX]]: this.state.dropdownPositionX,
        })

        const caption = this.renderCaption()

        const selectedItem = this.props.items.find((obj) => obj.value === this.state.selectedItem)

        let selectWidth = this.props.width
        if (!selectWidth) {
            selectWidth = this.state.selectWidth < MAX_WIDTH ? this.state.selectWidth : MAX_WIDTH
        }

        const dataAttrs = this.props.tooltipId && !this.state.isActive ? {
            'data-tip': true,
            'data-for': this.props.tooltipId,
        } : {}

        return (
            <div
                className={classNameBase}
                tabIndex={1}
                onBlur={this.handleBlur}
            >
                {caption}
                <div
                    ref={(c) => this._select = c}
                    className={styles.selectContainer}
                    {...dataAttrs}
                >
                    <div
                        className={styles.decorator}
                        style={{width: selectWidth}}
                    >
                        <div
                            className={styles.select}
                            onClick={this.props.isDisabled ? null : this.handleToggle}
                            onMouseDown={this.props.isDisabled ? null : sendSelectClickSoundCommand}
                            onMouseEnter={this.props.isDisabled ? null : sendHoverSoundCommand}
                        >
                            <div className={styles.selectedItem}>{selectedItem.caption}</div>
                        </div>
                    </div>
                    <menu
                        ref={(c) => this._dropdown = c}
                        className={classNameDropdown}
                    >
                        {this.renderItems()}
                    </menu>
                </div>
            </div>
        )
    }
}

Select.propTypes = propTypes
Select.defaultProps = defaultProps

export default Select



// WEBPACK FOOTER //
// ../node_modules/react-wot-bootstrap/lib/components/Select/Select.jsx